/**
 * Frontend accessor for the prices catalog.
 *
 * The JSON ships with the frontend bundle (Vite handles JSON imports
 * natively). In dev mode we validate it once against the Zod schema; in
 * production we trust the build pipeline (the same JSON was already
 * validated by the backend at boot, and the schema check costs ~10ms on
 * every cold load).
 */
import { PricesData, type Country, type PricesData as PricesDataT } from '@roamzy/shared';
// Vite resolves this JSON at build time. The relative path leaves the
// frontend package boundary intentionally — `product/data/prices.json` is
// the single source of truth shared with the backend.
import raw from '../../../data/prices.json';

const data: PricesDataT = import.meta.env.DEV
  ? PricesData.parse(raw)
  : (raw as PricesDataT);

const bySlug = new Map<string, Country>(data.countries.map((c) => [c.slug, c]));

export const allCountries: readonly Country[] = data.countries;

export function getCountryBySlug(slug: string): Country | undefined {
  return bySlug.get(slug);
}

/**
 * Returns the curated tourism-sorted countries, in order. Slugs that no
 * longer resolve (e.g. removed from the xlsx) are silently skipped.
 */
export function getTopTourism(): readonly Country[] {
  const out: Country[] = [];
  for (const slug of data.topTourismSorted) {
    const c = bySlug.get(slug);
    if (c) out.push(c);
  }
  return out;
}
export const pricesVersion = data.version;
